import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { EventSourceInput } from '@fullcalendar/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from 'ngx-kuv-tools';
import { Observable } from 'rxjs';
import { ConfigService } from 'src/app/utils/config/config.service';
import { DateService } from 'src/app/utils/date/date.service';
import { FormatterService } from 'src/app/utils/formatter/formatter.service';
import { HelperService } from 'src/app/utils/helper/helper.service';
import { ModalDeleteComponent } from 'src/app/utils/modal-delete/modal-delete.component';
import { ModalComponent } from 'src/app/utils/modal/modal.component';
import { SesionService } from 'src/app/utils/sesion/sesion.service';

@Injectable({
  providedIn: 'root'
})
export class RevisionService {
  controller: string = "revision/";
  tipoAceiteController: string = "tipo-aceite/";

  REVISION_A_COPIAR = 'revision-a-copiar';

  constructor(
    private alerts: AlertService,
    private helperService: HelperService,
    private http: HttpClient,
    private config: ConfigService,
    private sesion: SesionService,
    private formatter: FormatterService,
    private dateService: DateService,
    private modalService: NgbModal,
  ) { }

  indexFlujo(desde: string, hasta: string, no_completadas: boolean = false): Observable<any> {
    return this.http.post(this.config.apiUrl + this.controller + `index-flujo`, { desde, hasta, no_completadas }, this.sesion.headers);
  }

  insert(data: any): Observable<any> {
    return this.http.post(this.config.apiUrl + this.controller + 'crear', data, this.sesion.headers);
  }

  update(data: any): Observable<any> {
    return this.http.put(this.config.apiUrl + this.controller + 'actualizar/' + data.id, data, this.sesion.headers);
  }

  deactivate(id: any): Observable<any> {
    return this.http.delete(this.config.apiUrl + this.controller + 'desactivar/' + id, this.sesion.headers);
  }

  activate(id: any): Observable<any> {
    return this.http.delete(this.config.apiUrl + this.controller + "activar/" + id, this.sesion.headers);
  }

  delete(id: any): Observable<any> {
    return this.http.delete(this.config.apiUrl + this.controller + "eliminar/" + id, this.sesion.headers);
  }

  index_rol_select(): Observable<any> {
    return this.http.get(this.config.apiUrl + this.controller + 'ver-rol-select', this.sesion.headers);
  }

  view(id: number): Observable<any> {
    return this.http.get(this.config.apiUrl + this.controller + `view/${id}`, this.sesion.headers);
  }

  aceite_index_select(): Observable<any> {
    return this.http.get(this.config.apiUrl + this.tipoAceiteController + 'ver-select', this.sesion.headers);
  }

  fecha_estimada(form: any): Observable<any> {
    return this.http.post(this.config.apiUrl + this.controller + 'fecha_estimada', form, this.sesion.headers);
  }

  downloadPdf(id: number, tipo: number = 1): Observable<any> {
    if (tipo == 4) return this.http.get(this.config.apiUrl + this.controller + `download-pdf-servicios-sin-precios/${id}`, this.sesion.headersBlob);
    if (tipo == 3) return this.http.get(this.config.apiUrl + this.controller + `download-pdf-sin-precios/${id}`, this.sesion.headersBlob);
    if (tipo == 2) return this.http.get(this.config.apiUrl + this.controller + `download-pdf-neto/${id}`, this.sesion.headersBlob);
    return this.http.get(this.config.apiUrl + this.controller + `download-pdf/${id}`, this.sesion.headersBlob);
  }

  sendPdf(data: any): Observable<any> {
    return this.http.post(this.config.apiUrl + this.controller + `send-pdf`, data, this.sesion.headers);
  }

  cambiarEstado(data: any): Observable<any> {
    return this.http.put(this.config.apiUrl + this.controller + 'cambiar-estado/' + data.id, data, this.sesion.headers);
  }

  subirDocumento(id: number, file: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append(`file`, file, file.name);
    return this.http.put(this.config.apiUrl + this.controller + 'subir-documento/' + id, formData, this.sesion.headersFile);
  }

  subirDocumentos(id: number, files: File[]): Observable<any> {
    const formData: FormData = new FormData();
    files.forEach((file: File) => {
      formData.append(`files`, file, file.name);
    });
    return this.http.put(this.config.apiUrl + this.controller + 'subir-documentos/' + id, formData, this.sesion.headersFile);
  }

  eliminarDocumento(id: number): Observable<any> {
    return this.http.delete(this.config.apiUrl + this.controller + 'eliminar-documento/' + id, this.sesion.headers);
  }

  obtenerDocumentos(id: number): Observable<any> {
    return this.http.get(this.config.apiUrl + this.controller + 'obtener-documentos/' + id, this.sesion.headers);
  }

  oTsAsEventos(ots: any[]): EventSourceInput {
    return ots.map(ot => {
      let nombreCliente = 'Sin Cliente';

      if (ot.cliente) {
        nombreCliente = this.formatter.nombreCompleto(ot.cliente);
      } else if (ot.vehiculo.usuario) {
        nombreCliente = this.formatter.nombreCompleto(ot.vehiculo.usuario);
      }

      const patente = ot.vehiculo?.patente ?? 'Sin patente';

      let color = 'blue';
      const daysBetween = this.dateService.dateDiffFixed(new Date(), new Date(ot.fecha_estimada), 'days');
      if (daysBetween >= 0) {
        if (daysBetween <= 3) {
          color = 'orange';
        } else if (daysBetween <= 10) {
          color = 'green';
        }
      }

      return {
        id: ot.id,
        title: `${patente} - ${nombreCliente}`,
        start: this.dateService.formatUtcString(ot.fecha_estimada),
        end: this.dateService.formatUtcString(ot.fecha_estimada),
        backgroundColor: color,
        borderColor: color,
        textColor: 'white',
        groupId: 'ot',
      };
    }
    );
  }

  indexPorRenovar(fechaInicio: string, fechaTermino: string, sinLimiteFuturas = false): Observable<any> {
    const data = {
      fechaInicioEstimada: fechaInicio,
      fechaTerminoEstimada: fechaTermino,
      sinLimiteFuturas,
      filters: [],
      page: 1,
      pageSize: 10000,
      sorted: false,
      filtered: true,
      sorted_asc: true,
    };
    return this.http.post(this.config.apiUrl + this.controller + `ver-por-renovar`, data, this.sesion.headers);
  }

  unidadesProducto(producto: any): number | null {
    if (producto.tipo == 2) {
      return null;
    }
    return producto.stock_local;
  }

  unidadesProductoRemoto(producto: any): number | null {
    if (producto.tipo == 2) {
      return null;
    }
    return producto.stock_remoto;
  }

  debePagarPorCredito(revision: any,) {
    // revision finalizada, tipo pago es credito y si no ha pagado
    if (revision.estado == 3 && revision.tipo_pago == 6 && !this.helperService.decimalesIguales(revision.monto, revision.monto_pagado)) {
      // this.alerts.addAlert("Cambios guardados. El cliente aún no ha terminado de pagar por Crédito", "info");
      const modalRef = this.modalService.open(ModalComponent, { windowClass: 'clear-modal', size: 'lg', scrollable: true, backdrop: 'static' });
      modalRef.componentInstance.titulo = "Notificación de Pago de Crédito";
      modalRef.componentInstance.texto = "El cliente aún no ha terminado de pagar por Crédito";
      modalRef.componentInstance.soloAceptar = true;
      modalRef.componentInstance.textoAceptar = "Aceptar";
      modalRef.componentInstance.class_boton = "btn btn-primary"
      modalRef.result.then((result: any) => { }, (reason: any) => { });
    }
  }

  esOtFinalizadaYOperador(revision: any) {
    return revision.tipo === 1 && revision.estado === 3 && this.sesion.rol == this.sesion.ROL_OPERADOR;
  }

  cerrarModaSiOtFinalizadaYOperador(revision: any, activeModal: NgbActiveModal): void {
    if (this.esOtFinalizadaYOperador(revision)) {
      activeModal.dismiss();
      this.alerts.addAlert("Las Órdenes de Trabajo finalizadas no pueden ser editadas por los mecánicos", "warning");
    }
  }

  insertAnotacion(data: any): Observable<any> {
    return this.http.post(this.config.apiUrl + this.controller + 'crear-anotacion', data, this.sesion.headers);
  }

  updateAnotacion(data: any): Observable<any> {
    return this.http.put(this.config.apiUrl + this.controller + 'actualizar-anotacion/' + data.id, data, this.sesion.headers);
  }

  deleteAnotacion(id: any): Observable<any> {
    return this.http.delete(this.config.apiUrl + this.controller + "eliminar-anotacion/" + id, this.sesion.headers);
  }

  emitirDocumento(id: number, tipoDocumento: number, vencimiento: string): Observable<any> {
    const data = { tipo_documento: tipoDocumento, vencimiento: vencimiento };
    return this.http.put(this.config.apiUrl + this.controller + 'emitir-documento/' + id, data, this.sesion.headers);
  }

  cambiarEstadoPago(data: any): Observable<any> {
    return this.http.put(this.config.apiUrl + this.controller + 'cambiar-estado-pago/' + data.id, data, this.sesion.headers);
  }

  asignacionCostos(data: any): Observable<any> {
    return this.http.put(this.config.apiUrl + this.controller + 'asignacion-costos', data, this.sesion.headers);
  }
  tieneDocumentoAsociado(id: number): Observable<any> {
    return this.http.get(this.config.apiUrl + this.controller + 'tiene-documentos/' + id, this.sesion.headers);
  }
  verificarKeyRevision(key: string): Observable<any> {
    return this.http.get(this.config.apiUrl + this.controller + 'verificar-key/' + key, this.sesion.headers);
  }
  generarKeyRevision(revision: any): Observable<any> {
    return this.http.get(this.config.apiUrl + this.controller + 'generar-key/' + revision.id, this.sesion.headers);
  }
  ver(id: number): Observable<any> {
    return this.http.get(this.config.apiUrl + this.controller + 'ver/' + id, this.sesion.headers);
  }
  desvincularRevisionCaja(id: number): Observable<any> {
    return this.http.get(this.config.apiUrl + this.controller + 'eliminar-revision-caja/' + id, this.sesion.headers);
  }
  updateComision(data: any): Observable<any> {
    return this.http.put(this.config.apiUrl + this.controller + 'actualizar-comision/' + data.id, data, this.sesion.headers);
  }
  verUltimaOt(patente: string): Observable<any> {
    return this.http.get(this.config.apiUrl + this.controller + 'ultima-ot/' + patente, this.sesion.headers);
  }

}